<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { RoomPeer } from '../interfaces/Peer.interface';
import { Users, LogOut, Mic, MicOff, UserPlus } from 'lucide-vue-next';
import { useTranslations } from '../composables/useTranslations';
import { useLogger } from '../composables/useLogger';
import { AVMedia } from 'vue-audio-visual';

const t = useTranslations();
const { log } = useLogger();

const props = defineProps<{
  participants: RoomPeer[];
}>();

const amMuted = computed(() => {
  return props.participants.find((p) => p.isLocal)?.isMuted;
});

const emit = defineEmits<{
  (e: 'toggleMute', participant: RoomPeer): void;
  (e: 'leaveRoom'): void;
}>();

// todos:
// - handle peer removal better
// - allow mute every person
// - allow changing volume for every person

function leaveRoom() {
  emit('leaveRoom');
}

function toggleMute() {
  const localParticipant = props.participants.find((p) => p.isLocal);
  if (localParticipant) {
    emit('toggleMute', localParticipant);
  }
}

onMounted(() => {
  log('on mounted participant section');
});
</script>

<template class="relative">
  <div
    class="bg-white rounded-2xl shadow-sm min-h-0 border border-gray-100 flex flex-col relative"
  >
    <!-- Header -->
    <div class="p-4 border-b border-gray-100 flex items-center justify-between">
      <h2 class="font-bold text-gray-800 flex items-center text-sm">
        <Users :size="16" class="mr-2" />
        {{ t.participants }} ({{ participants?.length || 0 }}/5)
      </h2>
      <button
        class="text-xs py-1 px-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center gap-1 transition-colors"
        @click="leaveRoom()"
      >
        <LogOut :size="12" />
        {{ t.leave }}
      </button>
    </div>

    <!-- Participants list -->
    <div class="p-3 space-y-2 flex-grow overflow-y-auto">
      <div class="p-3 space-y-2 relative">
        <div
          v-for="participant in participants"
          :key="participant.sid"
          :class="[
            'flex items-center p-2 rounded-lg',
            participant.isLocal
              ? 'bg-[#dd7631]/10 border border-[#dd7631]/20'
              : 'hover:bg-gray-50',
          ]"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center bg-gray-200 mr-3"
          >
            {{
              participant.username
                ? participant.username.charAt(0).toUpperCase()
                : 'A'
            }}
          </div>

          <div class="min-w-0 flex-1">
            <p class="font-medium text-gray-900 text-sm truncate">
              {{ participant.username }}
            </p>

            <div class="flex items-center text-xs text-gray-500 mt-1">
              <div
                v-if="participant.isMuted"
                class="flex items-center mr-2 flex-shrink-0"
              >
                <MicOff :size="12" class="mr-1" />
                {{ t.muted }}
              </div>

              <AVMedia
                v-if="participant.stream"
                :media="participant.stream"
                type="frequ"
                frequ-direction="mo"
                line-color="#dd7631"
                :line-width="18"
                class="h-4 w-8"
                :class="{ hidden: participant.isMuted }"
              />
            </div>

            <audio
              v-if="participant.stream"
              :srcObject="participant.stream"
              :autoplay="!participant.isLocal"
              :muted="participant.isLocal"
              class="hidden"
            ></audio>
          </div>
        </div>

        <!-- Empty slots for remaining capacity -->
        <div
          v-for="index in Math.max(0, 5 - (participants?.length || 0))"
          :key="'empty-' + index"
          class="flex items-center p-2 rounded-lg hover:bg-gray-50 text-gray-400"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center bg-gray-200 mr-3"
          >
            <UserPlus :size="16" />
          </div>
          <div class="flex-grow">
            <p class="text-sm">{{ t.availableSlots }}</p>
          </div>
        </div>
      </div>

      <!-- Action section at the bottom -->
      <div class="p-3 border-t border-gray-100">
        <div class="flex items-center gap-2">
          <button
            class="rounded-lg flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 flex items-center justify-center gap-1 text-sm font-medium transition-colors"
            @click="toggleMute()"
          >
            <MicOff v-if="amMuted" :size="16" />
            <Mic v-else :size="16" />
            {{ amMuted ? t.unmute : t.mute }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
